<div class="ai-analysis-dialog">
  <!-- Header -->
  <div class="dialog-header">
    <div class="header-content">
      <mat-icon class="header-icon">psychology</mat-icon>
      <div class="header-text">
        <h2>AI Contract Analysis</h2>
        <span class="contract-name">{{ data.contractName }}</span>
      </div>
    </div>
    <button mat-icon-button (click)="close()" class="close-button">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Main Content -->
  <div class="dialog-content">
    <!-- Left Side: Document Viewer -->
    <div class="document-panel">
      @if (data.document) {
        <!-- Document Toolbar -->
        <div class="document-toolbar">
          <span class="document-title">{{ data.document.fileName }}</span>
          <span class="spacer"></span>
          <button mat-icon-button
                  (click)="downloadDocument()"
                  matTooltip="Download">
            <mat-icon>download</mat-icon>
          </button>
          <button mat-icon-button
                  (click)="openDocumentInNewTab()"
                  matTooltip="Open in new tab">
            <mat-icon>open_in_new</mat-icon>
          </button>
        </div>

        <!-- Document Content -->
        <div class="document-content">
          @if (data.document.isPdf || data.document.isImage || data.document.isOfficeDocument) {
            <iframe [src]="data.document.safeViewerUrl"
                    class="document-iframe"
                    [title]="data.document.fileName">
            </iframe>
          } @else {
            <div class="unsupported-file">
              <mat-icon class="large-icon">description</mat-icon>
              <p class="file-name">{{ data.document.fileName }}</p>
              <p class="file-info">Preview not available for this file type</p>
              <button mat-raised-button color="primary" (click)="openDocumentInNewTab()">
                <mat-icon>open_in_new</mat-icon>
                Open in New Tab
              </button>
            </div>
          }
        </div>
      } @else {
        <div class="document-placeholder">
          <mat-icon>description</mat-icon>
          <h3>No Document</h3>
          <p>No contract document available</p>
        </div>
      }
    </div>

    <!-- Right Side: Analysis Tabs -->
    <div class="analysis-panel">
      <mat-tab-group [(selectedIndex)]="selectedTabIndex" class="analysis-tabs">

        <!-- Summary Tab (shows AI-generated summary) -->
        <mat-tab label="Summary">
          <div class="tab-content">
            @if (analysisError) {
              <div class="analysis-error">
                <mat-icon color="warn">error_outline</mat-icon>
                <p>{{ analysisError }}</p>
              </div>
            }

            @if (hasAIAnalysis) {
              <!-- Analysis metadata -->
              @if (analyzedAt) {
                <div class="analysis-metadata">
                  <mat-icon>schedule</mat-icon>
                  <span>Analyzed: {{ getAnalyzedAtDisplay() }}</span>
                </div>
              }

              <!-- Natural Language Summary -->
              @if (extractedSummary) {
                <mat-card class="summary-card">
                  <mat-card-header>
                    <mat-icon mat-card-avatar>summarize</mat-icon>
                    <mat-card-title>Contract Summary</mat-card-title>
                  </mat-card-header>
                  <mat-card-content>
                    <p class="summary-text">{{ extractedSummary }}</p>
                  </mat-card-content>
                </mat-card>
              }

              <div class="ai-results-grid">
                <!-- Extracted Parties -->
                <mat-card class="ai-result-card">
                  <mat-card-header>
                    <mat-icon mat-card-avatar>people</mat-icon>
                    <mat-card-title>Contract Parties</mat-card-title>
                  </mat-card-header>
                  <mat-card-content>
                    @if (extractedParties.length > 0) {
                      <div class="parties-list">
                        @for (party of extractedParties; track party.name) {
                          <div class="party-item">
                            <div class="party-name">{{ party.name }}</div>
                            <div class="party-meta">
                              <mat-chip class="role-chip">{{ party.role }}</mat-chip>
                              <span class="confidence-badge" [class]="getConfidenceClass(party.confidence)">
                                {{ getConfidenceText(party.confidence) }} confidence
                              </span>
                            </div>
                          </div>
                        }
                      </div>
                    } @else {
                      <p class="no-data">AI did not identify any parties in this document</p>
                    }
                  </mat-card-content>
                </mat-card>

                <!-- Extracted Effective Date -->
                <mat-card class="ai-result-card half-width">
                  <mat-card-header>
                    <mat-icon mat-card-avatar>event</mat-icon>
                    <mat-card-title>Effective Date</mat-card-title>
                  </mat-card-header>
                  <mat-card-content>
                    @if (extractedEffectiveDate) {
                      <div class="extracted-value">
                        <div class="value-main">{{ extractedEffectiveDate.extracted }}</div>
                        @if (extractedEffectiveDate.normalized) {
                          <div class="value-normalized">
                            <span class="label">Normalized:</span>
                            {{ extractedEffectiveDate.normalized }}
                          </div>
                        }
                        <div class="value-meta">
                          <span class="confidence-badge" [class]="getConfidenceClass(extractedEffectiveDate.confidence)">
                            {{ getConfidenceText(extractedEffectiveDate.confidence) }} confidence
                          </span>
                        </div>
                        @if (extractedEffectiveDate.sourceText) {
                          <div class="source-text">
                            <span class="label">Source:</span>
                            <span class="quote">"{{ extractedEffectiveDate.sourceText }}"</span>
                          </div>
                        }
                      </div>
                    } @else {
                      <p class="no-data">AI did not find an effective date in this document</p>
                    }
                  </mat-card-content>
                </mat-card>

                <!-- Extracted Termination Notice Period -->
                <mat-card class="ai-result-card half-width">
                  <mat-card-header>
                    <mat-icon mat-card-avatar>notifications</mat-icon>
                    <mat-card-title>Termination Notice Period</mat-card-title>
                  </mat-card-header>
                  <mat-card-content>
                    @if (extractedNoticePeriod) {
                      <div class="extracted-value">
                        <div class="value-main">{{ extractedNoticePeriod.extracted }}</div>
                        <div class="value-normalized">
                          <span class="label">Normalized:</span>
                          {{ extractedNoticePeriod.normalizedDays }} days
                        </div>
                        <div class="value-meta">
                          <span class="confidence-badge" [class]="getConfidenceClass(extractedNoticePeriod.confidence)">
                            {{ getConfidenceText(extractedNoticePeriod.confidence) }} confidence
                          </span>
                        </div>
                        @if (extractedNoticePeriod.sourceText) {
                          <div class="source-text">
                            <span class="label">Source:</span>
                            <span class="quote">"{{ extractedNoticePeriod.sourceText }}"</span>
                          </div>
                        }
                      </div>
                    } @else {
                      <p class="no-data">AI did not find a termination notice period in this document</p>
                    }
                  </mat-card-content>
                </mat-card>
              </div>
            } @else if (!analysisError) {
              <div class="no-analysis">
                <mat-icon>psychology</mat-icon>
                <h3>No AI Analysis Available</h3>
                <p>AI analysis data is not available for this contract.</p>
              </div>
            }
          </div>
        </mat-tab>

        <!-- Overview Tab -->
        <mat-tab label="Overview">
          <div class="tab-content">
            @if (hasAIAnalysis) {
              <div class="overview-grid">
                <!-- Contract Parties (from Phase 1 extraction) -->
                @if (extractedParties.length > 0) {
                  <mat-card class="overview-card">
                    <mat-card-header>
                      <mat-icon mat-card-avatar>people</mat-icon>
                      <mat-card-title>Contract Parties</mat-card-title>
                    </mat-card-header>
                    <mat-card-content>
                      <div class="parties-list">
                        @for (party of extractedParties; track party.name) {
                          <div class="party-item">
                            <div class="party-name">{{ party.name }}</div>
                            <div class="party-meta">
                              <mat-chip class="role-chip">{{ party.role }}</mat-chip>
                            </div>
                          </div>
                        }
                      </div>
                    </mat-card-content>
                  </mat-card>
                }

                <!-- Contract Purpose -->
                @if (extractedOverview?.contractPurpose) {
                  <mat-card class="overview-card">
                    <mat-card-header>
                      <mat-icon mat-card-avatar>description</mat-icon>
                      <mat-card-title>Contract Purpose</mat-card-title>
                    </mat-card-header>
                    <mat-card-content>
                      <p>{{ extractedOverview.contractPurpose }}</p>
                    </mat-card-content>
                  </mat-card>
                }

                <!-- Effective Date (from Phase 1 extraction) -->
                @if (extractedEffectiveDate) {
                  <mat-card class="overview-card half-width">
                    <mat-card-header>
                      <mat-icon mat-card-avatar>event</mat-icon>
                      <mat-card-title>Effective Date</mat-card-title>
                    </mat-card-header>
                    <mat-card-content>
                      <p>{{ extractedEffectiveDate.extracted }}</p>
                      @if (extractedEffectiveDate.normalized) {
                        <p class="normalized-date">({{ extractedEffectiveDate.normalized }})</p>
                      }
                    </mat-card-content>
                  </mat-card>
                }

                <!-- Expiration Date -->
                @if (extractedOverview?.expirationDate) {
                  <mat-card class="overview-card half-width">
                    <mat-card-header>
                      <mat-icon mat-card-avatar>event_busy</mat-icon>
                      <mat-card-title>Expiration Date</mat-card-title>
                    </mat-card-header>
                    <mat-card-content>
                      <p>{{ extractedOverview.expirationDate.extracted }}</p>
                      @if (extractedOverview.expirationDate.normalized) {
                        <p class="normalized-date">({{ extractedOverview.expirationDate.normalized }})</p>
                      }
                    </mat-card-content>
                  </mat-card>
                }

                <!-- Notice to Facility -->
                @if (extractedOverview?.noticeToFacility) {
                  <mat-card class="overview-card">
                    <mat-card-header>
                      <mat-icon mat-card-avatar>local_hospital</mat-icon>
                      <mat-card-title>Notice to Facility</mat-card-title>
                    </mat-card-header>
                    <mat-card-content>
                      <p>{{ extractedOverview.noticeToFacility }}</p>
                    </mat-card-content>
                  </mat-card>
                }

                <!-- Notice to Vendor -->
                @if (extractedOverview?.noticeToVendor) {
                  <mat-card class="overview-card">
                    <mat-card-header>
                      <mat-icon mat-card-avatar>medical_services</mat-icon>
                      <mat-card-title>Notice to Vendor</mat-card-title>
                    </mat-card-header>
                    <mat-card-content>
                      <p>{{ extractedOverview.noticeToVendor }}</p>
                    </mat-card-content>
                  </mat-card>
                }

                <!-- Initial Term -->
                @if (extractedOverview?.initialTerm) {
                  <mat-card class="overview-card">
                    <mat-card-header>
                      <mat-icon mat-card-avatar>schedule</mat-icon>
                      <mat-card-title>Initial Term</mat-card-title>
                    </mat-card-header>
                    <mat-card-content>
                      <p>{{ extractedOverview.initialTerm.extracted }}</p>
                      @if (extractedOverview.initialTerm.normalizedMonths) {
                        <p class="normalized-date">({{ extractedOverview.initialTerm.normalizedMonths }} months)</p>
                      }
                    </mat-card-content>
                  </mat-card>
                }

                <!-- Termination Notice Period (from Phase 1 extraction) -->
                @if (extractedNoticePeriod) {
                  <mat-card class="overview-card">
                    <mat-card-header>
                      <mat-icon mat-card-avatar>notifications</mat-icon>
                      <mat-card-title>Termination Notice Period</mat-card-title>
                    </mat-card-header>
                    <mat-card-content>
                      <p>{{ extractedNoticePeriod.extracted }}</p>
                      <p class="normalized-date">({{ extractedNoticePeriod.normalizedDays }} days)</p>
                    </mat-card-content>
                  </mat-card>
                }
              </div>
            } @else {
              <div class="no-analysis">
                <mat-icon>psychology</mat-icon>
                <h3>No AI Analysis Available</h3>
                <p>Run AI analysis to extract contract overview information.</p>
              </div>
            }
          </div>
        </mat-tab>

        <!-- Review Tab -->
        <mat-tab label="Review">
          <div class="tab-content">
            @if (hasAIAnalysis && extractedKeySections) {
              @if (foundClauseCount > 0) {
                <div class="save-clauses-panel">
                  <div class="save-clauses-text">
                    <strong>Save these provisions to the contract</strong>
                    <p>
                      Copies the language below onto this contract's Provisions tab. This is a manual step
                      on purpose, so a person reviews the AI's work before it is saved to the contract.
                      Only empty provisions are filled in. Anything already on the contract is left as is.
                    </p>
                    @switch (clauseSaveState) {
                      @case ('saved') {
                        <p class="save-clauses-result success">
                          <mat-icon>check_circle</mat-icon>
                          Saved {{ clauseSaveAdded.length }} provision{{ clauseSaveAdded.length === 1 ? '' : 's' }}: {{ clauseSaveAdded.join(', ') }}.
                          @if (clauseSaveSkipped.length > 0) {
                            Left as is (already filled in): {{ clauseSaveSkipped.join(', ') }}.
                          }
                        </p>
                      }
                      @case ('nothing-to-add') {
                        <p class="save-clauses-result">
                          <mat-icon>info</mat-icon>
                          Nothing to add. Every provision found here is already filled in on the contract.
                        </p>
                      }
                      @case ('error') {
                        <p class="save-clauses-result error">
                          <mat-icon>error</mat-icon>
                          Could not save to the contract. Please try again.
                        </p>
                      }
                    }
                  </div>
                  <button mat-raised-button color="primary"
                          (click)="saveClausesToContract()"
                          [disabled]="clauseSaveState === 'saving' || clauseSaveState === 'saved' || clauseSaveState === 'nothing-to-add'">
                    @if (clauseSaveState === 'saving') {
                      <mat-spinner diameter="18" class="button-spinner"></mat-spinner>
                    } @else {
                      <mat-icon>save</mat-icon>
                    }
                    Save to Contract
                  </button>
                </div>
              }
              <div class="analysis-table-container">
                <table class="analysis-table">
                  <thead>
                    <tr>
                      <th class="col-key">Key Section</th>
                      <th class="col-risk">Risk Score</th>
                      <th class="col-content">Contract Language</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (item of getKeySectionsArray(); track item.key) {
                      <tr>
                        <td class="col-key">
                          <strong>{{ item.label }}</strong>
                          @if (item.section && item.section.pageReference) {
                            <span class="page-ref">(p. {{ item.section.pageReference }})</span>
                          }
                        </td>
                        <td class="col-risk">
                          <mat-chip class="risk-pending">
                            Not Calculated Yet
                          </mat-chip>
                        </td>
                        <td class="col-content">
                          @if (item.section && item.section.found && item.section.language) {
                            <p class="language-text">{{ item.section.language }}</p>
                          } @else {
                            <p class="language-text not-found">Not found and/or needs investigation</p>
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            } @else {
              <div class="no-analysis">
                <mat-icon>psychology</mat-icon>
                <h3>No AI Analysis Available</h3>
                <p>Run AI analysis to extract key contract sections.</p>
              </div>
            }
          </div>
        </mat-tab>

        <!-- Playbook Tab -->
        <mat-tab label="Playbook">
          <div class="tab-content">
            @if (playbookItems.length > 0) {
              <div class="analysis-table-container">
                <table class="analysis-table">
                  <thead>
                    <tr>
                      <th class="col-key">Provision Name</th>
                      <th class="col-risk">Contract Type(s)</th>
                      <th class="col-content">Recommended Language</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (item of playbookItems; track item.provName) {
                      <tr>
                        <td class="col-key">
                          <strong>{{ item.provName }}</strong>
                        </td>
                        <td class="col-risk">
                          {{ item.contractTypes }}
                        </td>
                        <td class="col-content">
                          <div class="language-text" [innerHTML]="item.recommendedLanguage"></div>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            } @else {
              <div class="playbook-empty">
                <mat-icon>menu_book</mat-icon>
                <p>No approved provisions apply to this contract type yet.</p>
              </div>
            }
          </div>
        </mat-tab>

        <!-- Analysis Tab (approved provisions vs. what the contract says) -->
        <mat-tab label="Analysis">
          <div class="tab-content">
            <div class="analysis-actions">
              @if (provisionAnalyzedAt && provisionComparisons.length > 0 && !provisionAnalysisRunning) {
                <span class="analysis-timestamp">Last analyzed {{ getProvisionAnalyzedAtDisplay() }}</span>
              }
              <span class="analysis-actions-spacer"></span>
              <button mat-raised-button color="primary"
                      (click)="runProvisionAnalysis()"
                      [disabled]="provisionAnalysisRunning || applicableProvisions.length === 0">
                <mat-icon>fact_check</mat-icon>
                {{ provisionComparisons.length > 0 ? 'Re-analyze' : 'Run Analysis' }}
              </button>
            </div>

            @if (applicableProvisions.length === 0) {
              <div class="playbook-empty">
                <mat-icon>fact_check</mat-icon>
                <p>No approved provisions apply to this contract type, so there's nothing to analyze.</p>
              </div>
            } @else if (provisionAnalysisRunning) {
              <div class="playbook-empty">
                <mat-progress-spinner mode="indeterminate" diameter="48"></mat-progress-spinner>
                <p>{{ provisionAnalysisStatus || 'Analyzing...' }}</p>
              </div>
            } @else if (provisionAnalysisError) {
              <div class="playbook-empty">
                <mat-icon color="warn">error</mat-icon>
                <p>{{ provisionAnalysisError }}</p>
              </div>
            } @else if (provisionComparisons.length > 0) {
              <app-provision-review-table [comparisons]="provisionComparisons"></app-provision-review-table>
            } @else {
              <div class="playbook-empty">
                <mat-icon>fact_check</mat-icon>
                <p>This contract hasn't been analyzed against the approved provisions yet. Click
                  <strong>Run Analysis</strong> to compare it against the
                  {{ applicableProvisions.length }} applicable approved provision(s).</p>
              </div>
            }
          </div>
        </mat-tab>

      </mat-tab-group>
    </div>
  </div>

  <!-- Footer -->
  <div class="dialog-footer">
    <button mat-raised-button (click)="close()">
      Close
    </button>
  </div>
</div>
